import type { BanditBundle, BanditArmBundle, BanditFeature, SignalContext } from "@tracking-web/shared";
// Subpath PURO (no el barrel): el SDK NO arrastra zod. Misma matemática que el
// entrenador (fuente única) → el encoding y el muestreo son idénticos a los que
// produjeron el posterior; si divergieran, el bandit se rompería en silencio.
import { buildEncoding, encodeContext, serve, type ArmPosterior } from "@tracking-web/shared/banditMath";
import { evaluate, triggerFiredByTransient, triggerUsesBackIntent } from "./engine.js";
import { normalizePagePath } from "./context.js";
import type { EmitFn } from "./adapters.js";

type Adapters = {
  apply: (action: BanditArmBundle["action"], ruleId: string, ephemeral?: boolean) => boolean;
};

// Tope de reintentos de pintado (mismo criterio que experiments): si el ancla nunca
// aparece en esa ruta, se desiste para no quemar CPU (la impresión ya se logueó).
const MAX_PAINT_RETRIES = 60;
// Sorteos del Monte-Carlo de propensity al servir. Suficientes para un IPS con suelo,
// sub-milisegundo con 3-6 arms.
const PROPENSITY_DRAWS = 200;

/* ─── Resolución del contexto del visitante a NIVELES (deben casar con bundle.features) ─── */

const SEARCH_HOSTS = ["google", "bing", "yahoo", "duckduckgo", "yandex", "ecosia", "baidu", "ask"];
const SOCIAL_HOSTS = ["facebook", "instagram", "twitter", "linkedin", "reddit", "pinterest", "tiktok", "youtube", "whatsapp", "telegram"];
/** ¿el host TERMINA en `suffix` como dominio (no como substring)? */
function endsWithHost(h: string, suffix: string): boolean {
  return h === suffix || h.endsWith("." + suffix);
}
export function classifySource(referrerHost: string): string {
  if (!referrerHost) return "direct";
  const h = referrerHost.toLowerCase();
  // Comparamos por ETIQUETAS del host (entre puntos), no por substring: así
  // "mybinghampton.edu" NO casa "bing", ni "prefix-x.com.evil.net" casa "x.com".
  const labels = h.split(".");
  if (labels.some((l) => SEARCH_HOSTS.indexOf(l) >= 0)) return "search";
  if (labels.some((l) => SOCIAL_HOSTS.indexOf(l) >= 0)) return "social";
  // Acortadores/dominios especiales que no son una etiqueta limpia.
  if (endsWithHost(h, "t.co") || endsWithHost(h, "x.com") || endsWithHost(h, "fb.me") || endsWithHost(h, "fb.com")) {
    return "social";
  }
  return "other";
}
/** Franja a partir de una hora (0-23). Separado para poder testearlo sin reloj. */
export function daypartFor(hr: number): string {
  if (hr < 6) return "night";
  if (hr < 12) return "morning";
  if (hr < 18) return "afternoon";
  return "evening";
}
function daypart(): string {
  let hr = 12;
  try {
    hr = new Date().getHours(); // hora LOCAL del visitante
  } catch {
    /* noop */
  }
  return daypartFor(hr);
}
function resolveLevel(name: string, s: SignalContext): string {
  switch (name) {
    case "device":
      return s.device; // "mobile" | "tablet" | "desktop"
    case "source":
      return classifySource(s.referrer);
    case "visitor":
      return s.visitCount > 1 ? "returning" : "new";
    case "daypart":
      return daypart();
    default:
      return ""; // feature desconocida → encodeContext la trata como referencia
  }
}
function resolveFeatures(features: BanditFeature[], s: SignalContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of features) out[f.name] = resolveLevel(f.name, s);
  return out;
}

/** Path comparable del bandit: si guardaron una URL completa, nos quedamos con el
 *  pathname; si guardaron un trozo ("/blog" o "blog"), lo normalizamos a path. */
export function banditPath(raw: string): string {
  let s = raw || "";
  if (/^https?:\/\//i.test(s)) {
    try {
      s = new URL(s).pathname;
    } catch {
      /* noop */
    }
  }
  return normalizePagePath(s);
}

/** ¿La página actual cae bajo la URL del bandit? Exacta, o prefijo de SECCIÓN
 *  ("/blog" casa "/blog/articulo") — nunca un substring arbitrario (no inyecta
 *  donde no toca). El bandit jamás toca otras URLs (límite duro). */
export function urlMatches(banditUrl: string, cur: string): boolean {
  const u = banditPath(banditUrl);
  if (u === cur) return true;
  return u !== "/" && cur.startsWith(u + "/");
}

/**
 * Capa BANDIT en el navegador. Por cada bandit `running` cuya URL casa con la página,
 * decide una variante por Thompson 1-D (sticky por anonId), la pinta con CLS=0 vía los
 * adapters, y emite `bandit_view` con la propensity y el snapshot de contexto. La
 * recompensa (IPVPS de la sesión) se une OFFLINE en el entrenador; aquí no se atribuye
 * nada. La decisión es 100% en el navegador contra el posterior del config cacheable.
 */
export function startBandits(opts: {
  bandits: BanditBundle[];
  anonId: string;
  ctx: { state: SignalContext };
  adapters: Adapters;
  emit: EmitFn;
  /** Aplaza un bandit cuyo trigger aún no se puede decidir (p.ej. usa el país y éste no tiene
   *  veredicto): true → NO servir/loguear todavía (bandit_view + propensity son irreversibles). */
  deferTrigger?: (trigger: NonNullable<BanditBundle["trigger"]>) => boolean;
}) {
  // `bandits` es reasignable: la Fase 3 (re-fetch de config en navegación SPA) lo
  // actualiza con `update()` si cambió configVersion, igual que las reglas/variantes.
  let bandits = opts.bandits;
  const { anonId, ctx, adapters, emit } = opts;
  const deferTrigger = opts.deferTrigger ?? (() => false);
  const applied = new Set<string>();
  // banditId → arm aún por pintar + si su popup es efímero (capturado al DECIDIR, no se recalcula en
  // cada reintento ni hace falta re-buscar el bandit en la lista, que además es mutable por refresh).
  const pendingAction = new Map<string, { arm: BanditArmBundle; ephemeral: boolean }>();
  const pendingAttempts = new Map<string, number>();

  function applyBandit(b: BanditBundle) {
    if (applied.has(b.id) || b.status !== "running") return;
    // Las variantes SOLO se inyectan en la URL del bandit; nunca en otras (límite duro).
    if (!urlMatches(b.url, ctx.state.pageUrl)) return;
    if (b.trigger && deferTrigger(b.trigger)) return; // país sin veredicto aún → no servir todavía
    if (b.trigger && !evaluate(b.trigger, ctx.state)) return; // condiciones extra (opcional)

    // Decisión: Thompson 1-D sticky por anonId (mismo visitante → misma variante).
    const enc = buildEncoding(b.features);
    const features = resolveFeatures(b.features, ctx.state);
    const x = encodeContext(enc, features);
    const posteriors: ArmPosterior[] = b.arms.map((a) => ({
      theta: a.posterior.theta,
      Ainv: a.posterior.aInv,
      n: a.posterior.n,
    }));
    const { index, propensity } = serve(posteriors, x, {
      alpha: b.alpha,
      seed: anonId + ":" + b.id,
      draws: PROPENSITY_DRAWS,
    });
    const arm = b.arms[index];
    applied.add(b.id);
    // Impresión = decisión (ITT): logueamos el arm servido + propensity + contexto AUNQUE
    // el pintado se difiera (DOM SPA aún sin montar). Una sola vez por impresión.
    emit("bandit_view", { banditId: b.id, armId: arm.id, propensity, features });
    const ephemeral = triggerFiredByTransient(b.trigger, ctx.state); // fijo al decidir (no cambia luego)
    if (arm.action.type !== "noop" && !adapters.apply(arm.action, `bandit:${b.id}:${arm.id}`, ephemeral)) {
      pendingAction.set(b.id, { arm, ephemeral }); // DOM sin montar → reintenta el PINTADO (no el log)
    }
  }

  /** Reintenta pintar variantes ya logueadas cuyo DOM no estaba listo. Con tope. */
  function applyPending() {
    for (const [id, { arm, ephemeral }] of Array.from(pendingAction)) {
      if (adapters.apply(arm.action, `bandit:${id}:${arm.id}`, ephemeral)) {
        pendingAction.delete(id);
        pendingAttempts.delete(id);
        continue;
      }
      const n = (pendingAttempts.get(id) || 0) + 1;
      pendingAttempts.set(id, n);
      if (n >= MAX_PAINT_RETRIES) pendingAction.delete(id); // desiste (el log ya salió)
    }
  }

  // Apply inicial (carga MPA / primer render SPA).
  for (const b of bandits) applyBandit(b);

  /** En cada cambio de señal: reintenta pintados pendientes y aplica bandits cuya URL
   *  ya casa (navegación SPA hacia la URL del bandit, o trigger que se cumple ahora). */
  function tick(_state: SignalContext) {
    if (pendingAction.size) applyPending();
    for (const b of bandits) if (!applied.has(b.id)) applyBandit(b);
  }

  /** Navegación SPA → página virtual nueva: olvida lo aplicado para que la URL nueva
   *  vuelva a decidir/inyectar. La asignación es determinista por anonId → mismo arm
   *  (sticky), así que re-decidir no cambia la variante del visitante. */
  function reset() {
    applied.clear();
    pendingAction.clear();
    pendingAttempts.clear();
  }

  /** Re-fetch de config en navegación SPA: reemplaza la lista (posteriors/status/altas/bajas
   *  pudieron cambiar) y olvida lo aplicado para re-decidir con lo nuevo. La asignación sigue
   *  siendo sticky por anonId, así que la variante del visitante no cambia salvo que cambie el
   *  posterior. */
  function update(next: BanditBundle[]) {
    bandits = next;
    reset();
  }

  /** ¿Un Atrás AHORA pintaría algún bandit? El back-guard lo consulta para no armar un centinela que
   *  no llegaría a pintar. Respeta el gating REAL: solo bandits `running`, NO ya aplicados, cuya URL
   *  CASA con la página (límite duro), con trigger de Atrás que se cumpliría con Atrás. (No se calcula
   *  si el arm servido sería noop — requeriría correr la decisión Thompson; armar de más ahí es raro.) */
  function armableOnBack(state: SignalContext): boolean {
    for (const b of bandits) {
      if (b.status !== "running" || applied.has(b.id)) continue;
      if (!urlMatches(b.url, state.pageUrl)) continue;
      const t = b.trigger;
      if (t && triggerUsesBackIntent(t) && evaluate(t, { ...state, backExitIntent: true })) return true;
    }
    return false;
  }

  return { tick, reset, update, armableOnBack };
}
